import type { Diff } from 'diff-match-patch';
import { diff_match_patch } from 'diff-match-patch';

import { DELETE_PLACEHOLDER, RANGE_END, RANGE_START } from './constants';

/**
 * Tokenizes text into words and whitespace runs, treating special characters
 * (RANGE_START, RANGE_END, DELETE_PLACEHOLDER) as separate atomic tokens.
 *
 * This is a fork of diff_linesToChars_ that splits on word boundaries instead
 * of newlines, with special handling for our marker characters.
 *
 * @see https://github.com/google/diff-match-patch/wiki/Line-or-Word-Diffs
 */
function tokenizeForWordDiff(text1: string, text2: string) {
  const tokenArray: string[] = [''];
  const tokenHash = new Map<string, string>();

  /**
   * Tokenizes text into four kinds of tokens:
   *  1. Special markers — RANGE_START, RANGE_END, DELETE_PLACEHOLDER (atomic, one each)
   *  2. Words — runs of characters that are neither whitespace, punctuation, nor markers
   *  3. Punctuation — runs of Unicode punctuation characters (.,!?"()[] etc.)
   *     Uses a negative lookahead to exclude special markers, which are themselves
   *     Unicode punctuation (⟬ is Ps, ⟭ is Pe) and would otherwise be captured
   *     greedily into punctuation runs (e.g., ".⟭" → one token instead of two).
   *  4. Whitespace — runs of whitespace characters (spaces, tabs, newlines)
   *
   * The `u` flag enables Unicode property escapes so `\p{P}` covers all
   * Unicode punctuation categories, not just ASCII.
   */
  const tokenPattern = new RegExp(
    `[${RANGE_START}${RANGE_END}${DELETE_PLACEHOLDER}]|[^\\s\\p{P}${RANGE_START}${RANGE_END}${DELETE_PLACEHOLDER}]+|(?:(?![${RANGE_START}${RANGE_END}${DELETE_PLACEHOLDER}])\\p{P})+|\\s+`,
    'gu'
  );

  function encode(text: string) {
    let chars = '';
    const tokens = text.match(tokenPattern) || [];
    for (const token of tokens) {
      if (tokenHash.has(token)) {
        chars += tokenHash.get(token);
      } else {
        const char = String.fromCodePoint(tokenArray.length);
        tokenArray.push(token);
        tokenHash.set(token, char);
        chars += char;
      }
    }
    return chars;
  }

  return {
    chars1: encode(text1),
    chars2: encode(text2),
    tokenArray,
  };
}

/**
 * Computes a word-level diff between two strings.
 *
 * Returns raw diffs WITHOUT semantic cleanup. Use this when you need to
 * preserve exact token boundaries (e.g., for placeholder handling in layerDiffs).
 *
 * @param oldText - The original text
 * @param newText - The new text
 * @returns Raw diff-match-patch Diff array at word granularity
 */
export function diffWordsRaw(oldText: string, newText: string): Diff[] {
  const dmp = new diff_match_patch();
  // Increase timeout for longer documents
  dmp.Diff_Timeout = 5;

  const { chars1, chars2, tokenArray } = tokenizeForWordDiff(oldText, newText);
  const rawDiffs: Diff[] = dmp.diff_main(chars1, chars2, false);

  // Decode the single-char tokens back into the original word/whitespace strings
  dmp.diff_charsToLines_(rawDiffs, tokenArray);

  return rawDiffs;
}

/**
 * Computes a word-level diff between two strings with semantic cleanup.
 *
 * Applies semantic cleanup on word tokens (before decoding) to avoid
 * character-level substring extraction, then applies lossless cleanup
 * after decoding to align boundaries with word/sentence edges.
 *
 * @param oldText - The original text
 * @param newText - The new text
 * @returns Semantically cleaned diff-match-patch Diff array at word granularity
 */
export function diffWordsSemantic(oldText: string, newText: string): Diff[] {
  const dmp = new diff_match_patch();
  // Increase timeout for longer documents
  dmp.Diff_Timeout = 5;

  const { chars1, chars2, tokenArray } = tokenizeForWordDiff(oldText, newText);
  const rawDiffs: Diff[] = dmp.diff_main(chars1, chars2, false);

  /**
   * Run diff_cleanupSemantic BEFORE decoding so it operates on word-level
   * tokens (each encoded char = one word). Running it after decoding would let
   * its internal diff_cleanupMerge extract common character substrings within
   * words (e.g. "maintain"/"retain" sharing "tain"), re-introducing
   * character-level splits.
   */
  dmp.diff_cleanupSemantic(rawDiffs);

  // Decode the single-char tokens back into the original word/whitespace strings
  dmp.diff_charsToLines_(rawDiffs, tokenArray);

  /**
   * Slide decoded diff boundaries to align with word/sentence edges LAST.
   * This is safe post-decode because it only repositions single edits between
   * equalities (no common-substring extraction like diff_cleanupMerge).
   */
  dmp.diff_cleanupSemanticLossless(rawDiffs);

  return rawDiffs;
}
